import { useEffect, useState } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefDataContext";
import { PageHeader, initials } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Save, Building2, Globe, MapPin, Shield } from "lucide-react";
import { toast } from "sonner";

const ROLES = Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));

export default function Settings() {
  const { user } = useAuth();
  const ref = useRefData();
  const [settings, setSettings] = useState({ brand_name: "", language: "English", default_country: "Denmark" });
  const [users, setUsers] = useState([]);
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    if (isSuper) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [isSuper]);

  const saveSettings = async () => {
    try {
      await api.put("/settings", {
        brand_name: settings.brand_name, language: settings.language, default_country: settings.default_country,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const changeRole = async (uid, role) => {
    try { await api.put(`/users/${uid}/role`, { role, location_id: null }); toast.success("Role updated"); setUsers(users.map((u) => u.user_id === uid ? { ...u, role } : u)); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure your Hundeverket workspace." />
      <Tabs defaultValue="general" className="max-w-3xl">
        <TabsList className="h-auto">
          <TabsTrigger value="general" data-testid="settings-tab-general">General</TabsTrigger>
          <TabsTrigger value="roles" data-testid="settings-tab-roles">Role Management</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4 space-y-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Building2 size={18} strokeWidth={1.5} /> Brand</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-1.5"><Label>Brand name</Label><Input value={settings.brand_name} onChange={(e) => setSettings({ ...settings, brand_name: e.target.value })} data-testid="settings-brand-name" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label className="flex items-center gap-1.5"><Globe size={13} /> System language</Label>
                  <Select value={settings.language} onValueChange={(v) => setSettings({ ...settings, language: v })}>
                    <SelectTrigger data-testid="settings-language"><SelectValue /></SelectTrigger>
                    <SelectContent>{["English", "Danish", "Swedish", "Norwegian"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label className="flex items-center gap-1.5"><MapPin size={13} /> Default country</Label>
                  <Input value={settings.default_country} onChange={(e) => setSettings({ ...settings, default_country: e.target.value })} data-testid="settings-country" />
                </div>
              </div>
              <Button onClick={saveSettings} className="gap-2" data-testid="settings-save-btn"><Save size={16} strokeWidth={1.5} /> Save changes</Button>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg">Location Defaults</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">You currently operate <span className="font-medium text-foreground">{ref.locations.length}</span> franchise location(s). Manage them from the Locations page.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Shield size={18} strokeWidth={1.5} /> Role Management</CardTitle></CardHeader>
            <CardContent>
              {!isSuper ? (
                <p className="text-sm text-muted-foreground">Role management is available to HV Super Admins only. Your access level: <span className="font-medium text-foreground">{ROLE_LABELS[user?.role]}</span>.</p>
              ) : (
                <div className="space-y-2">
                  {users.map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between p-3 rounded-lg border border-border/60" data-testid="user-role-row">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9"><AvatarImage src={u.picture} /><AvatarFallback className="bg-primary text-primary-foreground text-xs">{initials(u.full_name)}</AvatarFallback></Avatar>
                        <div><div className="text-sm font-medium">{u.full_name}</div><div className="text-xs text-muted-foreground">{u.email}</div></div>
                      </div>
                      <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                        <SelectTrigger className="w-48" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                  {users.length === 0 && <p className="text-sm text-muted-foreground">No users found.</p>}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
